import { useQuery } from '@tanstack/react-query';
import Checkbox from '@ui/Checkbox';
import SafeImage from '@ui/SafeImage';
import Select from '@ui/Select';
import { addNewNote, refundOrder } from '@util/firestore/orders';
import { chargeAccount } from '@util/firestore/payments';
import { getProductsByIds } from '@util/firestore/products';
import { formatCurrency } from '@util/index';
import { logError } from '@util/logError';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { OrderDocument } from 'models/order';
import React from 'react';
import { Button } from '..';
import BaseModal from './BaseModal';
import { useRouter } from 'next-nprogress-bar';

const CancelOrderModal = ({
  isOpen,
  dismiss,
  order,
}: {
  isOpen: boolean;
  dismiss: () => void;
  order: OrderDocument;
}) => {
  const { showErrorToast } = useToastContext();
  const { userDoc } = useAuth();
  const router = useRouter();
  const elligibleItems =
    order.sellers[userDoc?.uid ?? '']?.filter((item) => item.state === 0) ?? [];
  const [checkedItems, setCheckedItems] = React.useState(elligibleItems);

  const [showConfirm, setShowConfirm] = React.useState(false);

  const isFirst = userDoc?.metrics.num_refunded === 0;
  const fee = isFirst ? 0.05 : 0.1;
  const min = isFirst ? 5 : 10;

  const subTotal =
    checkedItems.reduce((acc, item) => acc + item.product_cost * item.qty, 0) -
    (order.coupon?.seller_id === userDoc?.uid ? order.coupon?.off ?? 0 : 0);
  const cancellationCost = Math.max(subTotal * fee, min);

  const [loading, setLoading] = React.useState(false);
  const { data: products } = useQuery({
    queryKey: ['products', ...elligibleItems.map((item) => item.id)],
    queryFn: () => getProductsByIds(elligibleItems.map((item) => item.id)),
    enabled: elligibleItems.length > 0,
  });
  const [refundId, setRefundId] = React.useState<string | undefined>(undefined);

  const [reason, setReason] = React.useState<
    'item_no_longer_in_stock' | 'shipping_complications' | 'out_of_town'
  >('item_no_longer_in_stock');
  return (
    <BaseModal
      isOpen={isOpen}
      dismiss={dismiss}
      title="Cancel Order"
      hideCloseIcon
    >
      <div className="flex grow flex-col justify-between gap-[1.6rem] p-8">
        {showConfirm ? (
          <>
            {loading ? (
              <strong>Cancelling Order...</strong>
            ) : (
              <>
                <strong>
                  You will be charged a {fee * 100}% cancellation fee. (Min
                  {formatCurrency(min)}) <br /> Would you like to continue?
                </strong>
                <span className="max-w-[50rem]">
                  In order to ensure the integrity of our marketplace, sellers
                  on MX Locker must adhere to our seller guidelines. <br />
                  Refunds leave a negative experience to our buyers.
                </span>
                <span className="whitespace-pre">
                  {' '}
                  Cancellation Cost:{' '}
                  <strong>{formatCurrency(cancellationCost)}</strong>
                </span>
                {/* warning for future cancelations */}
                {isFirst && (
                  <span className="text-red-500">
                    This is your first cancellation. Future cancellations will
                    be charged a 10% fee.
                  </span>
                )}
              </>
            )}
            <label>Refund Reason</label>
            <Select
              allowEmpty={false}
              placeholder="Select a reason"
              options={[
                {
                  label: 'Item no longer in stock',
                  value: 'item_no_longer_in_stock',
                  id: 'item_no_longer_in_stock',
                },
                {
                  label: 'Shipping complications',
                  value: 'shipping_complications',
                  id: 'shipping_complications',
                },
                {
                  label: 'Out of town',
                  value: 'out_of_town',
                  id: 'out_of_town',
                },
              ]}
              value={reason}
              onChange={(e) => setReason(e.value)}
            />
            <div className="flex justify-between gap-[1.6rem]">
              <Button text="No" onClick={() => setShowConfirm(false)} />
              <Button
                text="Yes"
                loading={loading}
                onClick={async () => {
                  if (loading) return;
                  try {
                    setLoading(true);
                    if (checkedItems[0].account_id && !refundId) {
                      const res = await chargeAccount({
                        account_id: checkedItems[0].account_id,
                        amount: cancellationCost,
                        description: `Order #${order.order_num} - Cancellation Fee`,
                        order_id: order.id,
                        order_num: order.order_num,
                      });
                      setRefundId(res);
                    }
                    const { chatId } = await refundOrder({
                      reason: reason,
                      product_ids: checkedItems.map((item) => item.id),
                      order,
                      amount_charged: cancellationCost,
                    });
                    router.push(`/dashboard/messages/${chatId}`);
                    setTimeout(() => {
                      window.location.reload();
                    }, 1000);
                  } catch (e) {
                    logError(
                      e,
                      undefined,
                      undefined,
                      `Cancel Order: ${order.id} - ${userDoc?.uid}`
                    );
                    showErrorToast((e as Error).message);
                  } finally {
                    setLoading(false);
                    dismiss();
                  }
                }}
                type="secondary"
              />
            </div>
          </>
        ) : (
          <>
            <strong>Which items would you like to cancel?</strong>
            {elligibleItems.map((item) => (
              <div
                key={item.id}
                className="flex w-full items-center gap-[1.6rem]"
              >
                <Checkbox
                  label={''}
                  onChange={() => {
                    if (
                      checkedItems.find((i) => i.id === item.id) === undefined
                    ) {
                      setCheckedItems([...checkedItems, item]);
                    } else {
                      setCheckedItems(
                        checkedItems.filter((i) => i.id !== item.id)
                      );
                    }
                  }}
                  selected={
                    checkedItems.find((i) => i.id === item.id) !== undefined
                  }
                />
                {!!products?.length && (
                  <SafeImage
                    className="rounded-brand"
                    alt={products.find((p) => p.id === item.id)?.title ?? ''}
                    src={products.find((p) => p.id)?.thumbnail}
                    width={64}
                    height={64}
                  />
                )}

                <strong className="max-w-[20rem] grow ">
                  {item.product_name}
                </strong>
              </div>
            ))}
            <div className="flex justify-between gap-[1.6rem]">
              <Button text="Close" onClick={() => dismiss()} type="tertiary" />
              <Button
                text="Proceed"
                onClick={() => {
                  if (checkedItems.length === 0) {
                    showErrorToast('Please select at least one item.');
                    return;
                  }
                  setShowConfirm(true);
                }}
                type="error"
              />
            </div>
          </>
        )}
      </div>
    </BaseModal>
  );
};

export default CancelOrderModal;
